import { Badge } from "@adfenix/alexandria";
import { Link, matchPath } from "react-router-dom";
import { PathMatch } from "react-router-dom";
import { MenuItem, SubMenu, useProSidebar } from "react-pro-sidebar";
import ReportingIcon from "../icons/ReportingIcon";
import { getIsAdfenixToken } from "atoms/auth";
import { useRecoilValue } from "recoil";
import { useTranslation } from "react-i18next";
import useUserInfo from "hooks/useUserInfo";
import UiCodeBranch from "@iconscout/react-unicons/icons/uil-code-branch";
import UiCog from "@iconscout/react-unicons/icons/uil-cog";
import UiBolt from "@iconscout/react-unicons/icons/uil-bolt";
import UiCloudDataConnection from "@iconscout/react-unicons/icons/uil-cloud-data-connection";
import UilCreditCard from "@iconscout/react-unicons/icons/uil-credit-card";
import UiLink from "@iconscout/react-unicons/icons/uil-link";
import UiBrowser from "@iconscout/react-unicons/icons/uil-browser";
import UiBasket from "@iconscout/react-unicons/icons/uil-shopping-basket";
import { userSelectedHq } from "atoms/auth";
import { GuardedMenuItem, GuardedSubMenu } from "../MenuGuards";
import { SRegularEnvelope, SRegularFileImage } from "@adfenix/icons";
import { useQuery } from "@tanstack/react-query";
import { RealmediaCoreServiceApi } from "api/services";

export const CustomerLinks = () => {
  const isAdfenixToken = useRecoilValue(getIsAdfenixToken);
  const { accessLevel } = useUserInfo();
  const { t } = useTranslation();

  const selectedHq = useRecoilValue(userSelectedHq);
  const { toggleSidebar } = useProSidebar();

  const matchesRoute = (route: string) =>
    matchPath(route, location.pathname)?.pattern.end;

  const matchHqAndOfficePath: PathMatch<"hqId" | "officeId"> | null = matchPath(
    {
      path: "/customer/hq/:hqId/office/:officeId/facebook/integration",
      end: true
    },
    location.pathname
  );

  const matchHqPath: PathMatch<"hqId"> | null = matchPath(
    { path: "/customer/hq/:hqId/facebook/integration", end: true },
    location.pathname
  );

  const matchAgentPath: PathMatch | null = matchPath(
    {
      path: "/agent/facebook/integration",
      end: true
    },
    location.pathname
  );

  const matchBillingPath: PathMatch<"hqId"> | null = matchPath(
    { path: "/customer/hq/:hqId/billing", end: true },
    location.pathname
  );

  const matchTriggerPath: PathMatch<"hqId"> | null = matchPath(
    { path: "/customer/hq/:hqId/triggers", end: true },
    location.pathname
  );

  const matchIntegrationPath: PathMatch<"hqId"> | null = matchPath(
    { path: "/customer/hq/:hqId/integrations", end: true },
    location.pathname
  );

  const matchOrderCampaignPath: PathMatch<"hqId"> | null = matchPath(
    { path: "/customer/hq/:hqId/order-campaign", end: true },
    location.pathname
  );

  const matchCampaignsPath: PathMatch<"hqId"> | null = matchPath(
    { path: "/customer/hq/:hqId/campaigns", end: true },
    location.pathname
  );

  const matchPropertyPath: PathMatch<"hqId"> | null = matchPath(
    { path: "/customer/hq/:hqId/property", end: true },
    location.pathname
  );

  const matchEmailPath: PathMatch<"hqId"> | null = matchPath(
    { path: "/customer/hq/:hqId/email", end: true },
    location.pathname
  );

  const { data } = useQuery(
    ["real-media-link", selectedHq?.id],
    () =>
      RealmediaCoreServiceApi.login.authV1LoginPhoenixDetail(selectedHq!.id),
    { enabled: !!selectedHq?.id && accessLevel === "Multi-Brand" }
  );

  return (
    <>
      {!!data && (
        <SubMenu
          className="sidebar-top-menu-item"
          icon={<SRegularFileImage fill="currentColor" height={"24px"} />}
          label={t("sidebar.menu.media.title")}
          onClick={() => window.location.replace(data.redirectUrl)}
        ></SubMenu>
      )}

      <GuardedMenuItem
        requiredPermission={["Program", "View"]}
        onClick={() => toggleSidebar(false)}
        icon={<UiCodeBranch />}
        className="sidebar-top-menu-item"
        active={
          matchesRoute("/customer/hq/:hqId/program-builder/:programId") ||
          matchesRoute("/customer/hq/:hqId/program-builder")
        }
        component={
          <Link
            to={`/customer/hq/${
              selectedHq ? selectedHq.id : ":hqId"
            }/program-builder`}
          />
        }
      >
        Program Builder
      </GuardedMenuItem>

      <GuardedMenuItem
        requiredPermission={["Campaign", "Order"]}
        onClick={() => toggleSidebar(false)}
        icon={<UiBasket />}
        active={!!matchOrderCampaignPath}
        className="sidebar-top-menu-item"
        component={
          <Link to={`/customer/hq/${selectedHq?.id}/order-campaign `} />
        }
      >
        {t("order.page.subtitle")}
      </GuardedMenuItem>

      <MenuItem
        onClick={() => toggleSidebar(false)}
        active={!!matchCampaignsPath}
        icon={<UiBrowser />}
        className="sidebar-top-menu-item"
        component={<Link to={`/customer/hq/${selectedHq?.id}/campaigns`} />}
      >
        {t("sidebar.customer.menuItem.header.campaigns")}
      </MenuItem>

      <MenuItem
        onClick={() => toggleSidebar(false)}
        icon={<i className="uil uil-building is-size-4" />}
        active={!!matchPropertyPath}
        className="sidebar-top-menu-item"
        component={<Link to={`/customer/hq/${selectedHq?.id}/property`} />}
      >
        {t("listings.heading.title")}
      </MenuItem>

      <GuardedMenuItem
        requiredPermission={["Reports", "View"]}
        onClick={() => toggleSidebar(false)}
        icon={<ReportingIcon />}
        active={location.pathname === "/analytics" ? true : undefined}
        className="sidebar-top-menu-item"
        suffix={
          <Badge lightness="darker" color="primary">
            Beta
          </Badge>
        }
        component={<Link to={isAdfenixToken ? "/login" : "/analytics"} />}
      >
        {t("navigation.reporting.title")}
      </GuardedMenuItem>

      <GuardedSubMenu
        requiredPermission={["SelfServe", "Edit"]}
        className="sidebar-top-menu-item"
        defaultOpen={
          matchHqPath != null ||
          matchHqAndOfficePath != null ||
          matchAgentPath != null
            ? true
            : false
        }
        icon={<UiCog />}
        label={t("sidebar.menu.settings.title")}
      >
        <GuardedMenuItem
          requiredAccessLevel={["HQ", "Multi-Brand"]}
          onClick={() => toggleSidebar(false)}
          active={matchHqPath != null ? true : undefined}
          icon={<UiBolt />}
          className="sidebar-sub-menu-item"
          component={
            <Link
              to={
                isAdfenixToken
                  ? "/login"
                  : "/customer/hq/:hqId/facebook/integration"
              }
            />
          }
        >
          {t("selfonboarding.page.header.title")}
        </GuardedMenuItem>

        <GuardedMenuItem
          requiredAccessLevel={["Multi-Brand", "Office"]}
          onClick={() => toggleSidebar(false)}
          active={matchHqAndOfficePath != null ? true : undefined}
          icon={<UiBolt />}
          className="sidebar-sub-menu-item"
          component={
            <Link
              to={
                isAdfenixToken
                  ? "/login"
                  : "/customer/hq/:hqId/office/:officeId/facebook/integration"
              }
            />
          }
        >
          {accessLevel == "Multi-Brand"
            ? "Office social integration"
            : t("selfonboarding.page.header.title")}
        </GuardedMenuItem>

        <GuardedMenuItem
          requiredAccessLevel={["HQ", "Multi-Brand", "Office"]}
          onClick={() => toggleSidebar(false)}
          active={matchBillingPath !== null ? true : undefined}
          icon={<UilCreditCard />}
          className="sidebar-sub-menu-item"
          component={
            <Link
              to={isAdfenixToken ? "/login" : "/customer/hq/:hqId/billing"}
            />
          }
        >
          Billing
        </GuardedMenuItem>

        <GuardedMenuItem
          requiredPermission={["Program", "View"]}
          onClick={() => toggleSidebar(false)}
          active={matchTriggerPath !== null}
          icon={<UiCloudDataConnection />}
          className="sidebar-sub-menu-item"
          component={
            <Link
              to={
                isAdfenixToken
                  ? "/login"
                  : `/customer/hq/${
                      selectedHq ? selectedHq.id : ":hqId"
                    }/triggers`
              }
            />
          }
        >
          Triggers
        </GuardedMenuItem>

        <GuardedMenuItem
          onClick={() => toggleSidebar(false)}
          requiredAccessLevel={["Multi-Brand"]}
          active={!!matchIntegrationPath}
          icon={<UiLink />}
          className="sidebar-sub-menu-item"
          component={
            <Link
              to={isAdfenixToken ? "/login" : `/customer/hq/:hqId/integrations`}
            />
          }
        >
          Integrations
        </GuardedMenuItem>
        <GuardedMenuItem
          onClick={() => toggleSidebar(false)}
          requiredPermission={["SelfServe", "View"]}
          active={!!matchEmailPath}
          icon={<SRegularEnvelope fill="currentColor" />}
          className="sidebar-sub-menu-item"
          component={
            <Link to={isAdfenixToken ? "/login" : `/customer/hq/:hqId/email`} />
          }
        >
          {t("sidebar.customer.menuItem.header.email")}
        </GuardedMenuItem>
      </GuardedSubMenu>
    </>
  );
};
